const PLAYABLE_AUDIO_EXTENSIONS = ['m4a', 'mp3', 'mpa', 'wav', 'webm']

const AUDIO_MIME_TYPES = {
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  mpa: 'audio/mpeg',
  wav: 'audio/wav',
  webm: 'audio/webm'
}

export const isPlayableAudioFile = extension => PLAYABLE_AUDIO_EXTENSIONS.includes((extension || '').toLowerCase())

export const getAudioMimeType = extension => AUDIO_MIME_TYPES[(extension || '').toLowerCase()] || 'audio/mpeg'

export const canBrowserPlayAudio = extension => {
  const mimeType = AUDIO_MIME_TYPES[(extension || '').toLowerCase()]

  if (!mimeType) return false

  return document.createElement('audio').canPlayType(mimeType) !== ''
}

export const formatTime = seconds => {
  if (!seconds || !isFinite(seconds)) return '0:00'

  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)

  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Seconds of the recording actually heard: seeking ahead doesn't count as
// listening, and replaying a part doesn't count it twice.
export const playedSeconds = audio => {
  const ranges = audio.played

  if (!ranges) return 0

  let total = 0

  for (let index = 0; index < ranges.length; index += 1)
    total += ranges.end(index) - ranges.start(index)

  return total
}

// Browser-recorded webm files (MediaRecorder) have no duration in their header,
// so the audio element reports Infinity. Seeking far past the end forces the
// browser to compute the real duration before playback starts.
export const resolveAudioDuration = audio => new Promise(resolve => {
  const listeners = []

  const cleanup = () => listeners.forEach(([event, handler]) => audio.removeEventListener(event, handler))

  const onDurationChange = () => {
    if (!isFinite(audio.duration)) return

    cleanup()
    audio.currentTime = 0
    resolve()
  }

  const onLoadedMetadata = () => {
    if (isFinite(audio.duration)) {
      cleanup()
      resolve()
      return
    }

    audio.currentTime = 1e101
  }

  const onError = () => {
    cleanup()
    resolve()
  }

  listeners.push(['loadedmetadata', onLoadedMetadata], ['durationchange', onDurationChange], ['error', onError])
  listeners.forEach(([event, handler]) => audio.addEventListener(event, handler))

  if (audio.readyState >= 1) onLoadedMetadata()
})

export const base64ToBlob = (base64Data, mimeType) => {
  const byteCharacters = atob(base64Data.replace(/\s/g, ''))
  const byteNumbers = new Array(byteCharacters.length)

  for (let index = 0; index < byteCharacters.length; index++)
    byteNumbers[index] = byteCharacters.charCodeAt(index)

  const byteArray = new Uint8Array(byteNumbers)

  return new Blob([byteArray], {type: mimeType})
}
